import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { formatMoney } from "../consumption/formatters";
import type { RankedRow } from "./organisationInsights";
import styles from "./RankedList.module.css";
import { Tooltip } from "../shared/Tooltip";

/**
 * Rows ranked by spend, each as its name, a bar against the largest, its spend and change on last month, and a line of figures
 * beneath: its share, its people and spend per person, and any note. Used in place of a table on the Organisation page: the order
 * and the relative sizes are what a reader takes from it, and the figures sit beside each bar. With `limit`, only the largest are
 * listed, and the rest are summed in one line with a button that opens them all.
 *
 * @param props.rows - The rows, largest first.
 * @param props.currency - The currency the spend is in.
 * @param props.label - What the list ranks, for assistive technology.
 * @param props.limit - How many rows to list; all of them when omitted.
 * @param props.onShowAll - Opens every row; the "show all" line appears only with it, and only when rows are left out.
 * @param props.unitName - What one row is, plural, for the "show all" line (`divisions`).
 * @param props.highlight - A row to mark, such as the one a search found.
 */
export function RankedList({
  rows,
  currency,
  label,
  limit,
  onShowAll,
  unitName,
  highlight,
}: {
  rows: RankedRow[];
  currency: string;
  label: string;
  limit?: number;
  onShowAll?: () => void;
  unitName?: string;
  highlight?: string | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const shown = limit === undefined ? rows : rows.slice(0, limit);
  const rest = rows.slice(shown.length);
  const restSpend = rest.reduce((sum, row) => sum + (row.spend ?? 0), 0);
  const largest = Math.max(0, ...rows.map((row) => row.spend ?? 0));
  // Where any row has a change, every row keeps the chip's room, so the spend figures line up down the list.
  const reserveChange = rows.some((row) => row.change !== null);

  return (
    <div className={styles.wrapper}>
      <ol className={styles.list} aria-label={label}>
        {shown.map((row, index) => {
          const figures = [
            t("organisation.ranked.share", { percent: formatShare(row.sharePercent) }),
            row.people !== null ? t("organisation.ranked.people", { count: row.people }) : null,
            row.perPerson !== null ? t("organisation.ranked.perPerson", { amount: formatMoney(row.perPerson, currency) }) : null,
            row.detail ?? null,
          ].filter((figure): figure is string => figure !== null);

          return (
            <li
              key={row.key}
              className={`${styles.row} ${row.href ? styles.linked : ""} ${highlight === row.key ? styles.highlight : ""}`}
              style={{ "--row-index": Math.min(index, 12) } as React.CSSProperties}
            >
              <div className={styles.head}>
                {/* A row with a page has its name as the one link, stretched over the whole row, so any part of it opens the page. */}
                {row.href ? (
                  <Link to={row.href} className={`${styles.label} ${styles.link}`}>
                    {row.label}
                  </Link>
                ) : (
                  <span className={styles.label}>{row.label}</span>
                )}
                <span className={styles.figure}>
                  {row.spend !== null ? formatMoney(row.spend, currency) : t("organisation.notYetAvailable")}
                  <Change change={row.change} reserve={reserveChange} />
                </span>
              </div>
              {/* Decorative: the figures beside it say the same. */}
              <span className={styles.bar} aria-hidden="true">
                <span style={{ width: `${String(largest > 0 ? ((row.spend ?? 0) / largest) * 100 : 0)}%` }} />
              </span>
              <span className={styles.figures}>{figures.join(" · ")}</span>
            </li>
          );
        })}
      </ol>
      {onShowAll && rest.length > 0 && (
        <div className={styles.rest}>
          <span>{t("organisation.ranked.rest", { count: rest.length, amount: formatMoney(restSpend, currency) })}</span>
          <button type="button" className={styles.showAll} onClick={onShowAll}>
            {t("organisation.ranked.showAll", { count: rows.length, unit: unitName ?? "" })}
            <span aria-hidden="true"> →</span>
          </button>
        </div>
      )}
    </div>
  );
}

/** A share as text: a decimal place below 10%, where it still tells rows apart. */
function formatShare(percent: number): string {
  return percent.toFixed(percent < 10 ? 1 : 0);
}

/**
 * A row's change in usage on the same days of last month, as an arrow and a whole percentage; nothing when there is no comparison.
 *
 * @param props.change - The change as a fraction, or null.
 * @param props.reserve - Whether to keep the chip's room when there is no change, so the figures beside it line up with their neighbours'.
 */
function Change({ change, reserve }: { change: number | null; reserve: boolean }): React.JSX.Element | null {
  const { t } = useTranslation();
  if (change === null) {
    return reserve ? <span className={`${styles.change} ${styles.noChange}`} aria-hidden="true" /> : null;
  }

  const percent = Math.round(Math.abs(change) * 100);
  const direction = percent === 0 ? "flat" : change > 0 ? "up" : "down";
  // From three times last month's usage, a multiple reads better than a percentage in the thousands.
  const times = change >= 2 ? Math.round(change + 1) : null;
  const text = times !== null ? t("organisation.ranked.change.times", { times }) : t(`organisation.ranked.change.${direction}`, { percent });
  const shown = times !== null ? `×${String(times)}` : `${String(percent)}%`;

  return (
    <span className={`${styles.change} ${styles[direction]}`}>
      <Tooltip label={text}>
        <span aria-hidden="true">{direction === "up" ? "▲" : direction === "down" ? "▼" : "■"} {shown}</span>
        <span className="visually-hidden">{text}</span>
      </Tooltip>
    </span>
  );
}
